<script>
import Layout from '@layouts/main.vue'
import {
  authComputed,
  authMethods,
  dutyComputed,
  registrationComputed,
  profileMethods,
  dutyMethods,
  registrationMethods,
  oncallDutyMethods,
  userFileMethods,
  userFileComputed,
} from '@state/helpers'
import dayjs from 'dayjs'
import sortBy from 'lodash/sortBy'

export default {
  page() {
    return {
      title: this.currentUser.first_name,
    }
  },

  components: { Layout },

  data() {
    return {
      email: '',
      phone: '',
      oldPassword: '',
      newPassword: '',
      contactErrorMessage: '',
      passwordErrorMessage: '',
      isLoadingContactInfo: false,
      isLoadingPasswordChange: false,
      isSaved: false,
      failedField: null,
      failedOldPassword: false,
      tab: 'info',
    }
  },

  computed: {
    ...authComputed,

    ...dutyComputed,

    ...registrationComputed,

    ...userFileComputed,

    events() {
      if (!this.registrations || !this.registrations.length) {
        return []
      }

      return this.registrations.filter(
        (registration) =>
          dayjs(registration.event_date) > dayjs() &&
          registration.event_purpose !== 'Tęstiniai mokymai',
      )
    },

    studies() {
      if (!this.registrations || !this.registrations.length) {
        return []
      }

      return this.registrations.filter(
        (registration) =>
          dayjs(registration.event_date) < dayjs() &&
          registration.event_purpose === 'Tęstiniai mokymai',
      )
    },

    sortedDuties() {
      if (!this.duties || !this.duties.length) {
        return []
      }

      const mutatedDuties = this.duties.map((duty) => {
        const paddedHours =
          duty.hours.split('-')[0].length === 1 ? `0${duty.hours}` : duty.hours
        return {
          ...duty,
          dateTime: `${duty.date} ${paddedHours}`,
        }
      })

      return sortBy(mutatedDuties, ['dateTime'])
    },
  },

  created() {
    this.loadFormDefaultValues()
    this.fetchData()
  },

  methods: {
    ...authMethods,

    ...profileMethods,

    ...dutyMethods,

    ...registrationMethods,

    ...oncallDutyMethods,

    ...userFileMethods,

    fetchData() {
      this.$Progress.start()
      Promise.all([
        this.fetchAllDuties(),
        this.fetchAllRegistrations({ start_date: '2020-06-01' }),
        this.fetchAllOncallDuties(),
        this.fetchUserFiles(),
      ])
        .then(() => this.$Progress.finish())
        .catch(() => this.$Progress.fail())
    },

    fetchUserFiles() {
      this.fetchAllUserFiles(this.currentUser.id)
    },

    loadFormDefaultValues() {
      this.email = this.currentUser.email
      this.phone = this.currentUser.phone
    },

    guardFromUnfilledEmail() {
      if (!this.email.trim()) {
        this.failedField = 'email'
        this.contactErrorMessage = 'El. paštas negali būti tuščias.'
        return true
      }

      return false
    },

    guardFromUnfilledPhone() {
      if (!this.phone.trim()) {
        this.failedField = 'phone'
        this.contactErrorMessage = 'Telefonas negali būti tuščias.'
        return true
      }

      return false
    },

    changedFormFields() {
      const payload = {}

      if (this.email !== this.currentUser.email) {
        payload.email = this.email
      }

      if (this.phone !== this.currentUser.phone) {
        payload.phone = this.phone
      }

      return payload
    },

    onSubmitContactForm() {
      if (this.guardFromUnfilledEmail()) {
        return
      }

      if (this.guardFromUnfilledPhone()) {
        return
      }

      this.resetFormState()

      const payload = this.changedFormFields()

      if (!Object.keys(payload).length) {
        this.contactErrorMessage = 'Nieko nepakeitėte formoje.'
        return
      } else {
        this.isLoadingContactInfo = true
      }

      this.updateProfile(payload)
        .then(this.resetFormState)
        .then(() => (this.isSaved = true))
    },

    onSubmitPasswordForm() {
      if (!this.oldPassword.trim()) {
        this.failedField = 'old_password'
        this.passwordErrorMessage = 'Neįvedėte seno slaptažodžio.'
        return
      }

      if (!this.phone.trim()) {
        this.failedField = 'new_password'
        this.passwordErrorMessage = 'Neįvedėte naujo slaptažodžio'
        return
      }

      this.resetFormState()

      const payload = {}
      if (this.oldPassword && this.newPassword) {
        payload.old_password = this.oldPassword
        payload.new_password = this.newPassword
        this.isLoadingPasswordChange = true
      }

      if (!Object.keys(payload).length) {
        this.passwordErrorMessage =
          'Turite ką nors pakeisti, jeigu norite atnaujinti duomenis.'
        return
      }

      this.updateProfile(payload)
        .then(this.resetFormState)
        .then(() => (this.isSaved = true))
        .catch((error) => {
          this.failedOldPassword = error.response.data.error.includes(
            'Invalid old password',
          )
        })
    },

    resetFormState() {
      this.passwordErrorMessage = ''
      this.contactErrorMessage = ''
      this.failedField = ''
      this.isLoadingPasswordChange = false
      this.isLoadingContactInfo = false
      this.isSaved = false
    },

    onFileChange(file) {
      if (!file) {
        return
      }

      this.isLoadingContactInfo = true
      this.uploadAvatar(file).then(() => {
        this.reloadLogin()
        this.isLoadingContactInfo = false
      })
    },

    onChangeTab(tab) {
      this.tab = tab
      this.resetFormState()

      if (tab === 'hours') {
        this.fetchAllDuties({ noshift: true })
      } else {
        this.fetchAllDuties()
      }
    },
  },
}
</script>

<template>
  <Layout>
    <base-page-title>
      <template v-slot:title>
        <div class="text-2xl font-bold"> Mano paskyra </div>
      </template>
    </base-page-title>

    <div>
      <div class="flex flex-col lg:flex-row text-lg font-semibold">
        <h2
          class="mb-4 lg:mb-0 border border-t border-r border-l rounded lg:rounded-b-none px-4 py-2 cursor-pointer mr-4"
          :class="{
            'bg-gray-200': tab === 'info',
            'opacity-75': tab !== 'info',
          }"
          @click="onChangeTab('info')"
        >
          Informacija
        </h2>
        <h2
          class="mb-4 lg:mb-0 border border-t border-r border-l rounded lg:rounded-b-none px-4 py-2 cursor-pointer mr-4"
          :class="{
            'bg-gray-200': tab === 'contacts',
            'opacity-75': tab !== 'contacts',
          }"
          @click="onChangeTab('contacts')"
        >
          Kontaktai
        </h2>
        <h2
          class="mb-4 lg:mb-0 border border-t border-r border-l rounded lg:rounded-b-none px-4 py-2 cursor-pointer mr-4"
          :class="{
            'bg-gray-200': tab === 'password',
            'opacity-75': tab !== 'password',
          }"
          @click="onChangeTab('password')"
        >
          Pakeisti slaptažodį
        </h2>
        <h2
          v-if="duties && duties.length"
          class="mb-4 lg:mb-0 border border-t border-r border-l rounded lg:rounded-b-none px-4 py-2 cursor-pointer mr-4"
          :class="{
            'bg-gray-200': tab === 'duties',
            'opacity-75': tab !== 'duties',
          }"
          @click="onChangeTab('duties')"
        >
          Budėjimai ir galimybės
        </h2>
        <h2
          v-if="events.length"
          class="mb-4 lg:mb-0 border border-t border-r border-l rounded lg:rounded-b-none px-4 py-2 cursor-pointer mr-4"
          :class="{
            'bg-gray-200': tab === 'events',
            'opacity-75': tab !== 'events',
          }"
          @click="onChangeTab('events')"
        >
          Renginiai
        </h2>
        <h2
          v-if="studies.length"
          class="mb-4 lg:mb-0 border border-t border-r border-l rounded lg:rounded-b-none px-4 py-2 cursor-pointer mr-4"
          :class="{
            'bg-gray-200': tab === 'studies',
            'opacity-75': tab !== 'studies',
          }"
          @click="onChangeTab('studies')"
        >
          Mokymai
        </h2>
        <h2
          v-if="events.length"
          class="mb-4 lg:mb-0 border border-t border-r border-l rounded lg:rounded-b-none px-4 py-2 cursor-pointer mr-4"
          :class="{
            'opacity-75': tab !== 'events',
            'bg-gray-200': tab === 'hours',
          }"
          @click="onChangeTab('hours')"
        >
          Papildomos val.
        </h2>
        <h2
          v-if="userFiles && userFiles.length"
          class="mb-4 lg:mb-0 border border-t border-r border-l rounded lg:rounded-b-none px-4 py-2 cursor-pointer mr-4"
          :class="{
            'opacity-75': tab !== 'files',
            'bg-gray-200': tab === 'files',
          }"
          @click="onChangeTab('files')"
        >
          Dokumentai
        </h2>
      </div>

      <div class="lg:border pt-8 lg:p-8">
        <div v-if="tab === 'info'">
          <div class="flex flex-col lg:flex-row">
            <div class="w-full lg:w-1/3 mb-4">
              <div class="text-center">
                <base-avatar
                  :filename="currentUser.avatar_thumb"
                  class="w-48 h-48 mx-auto"
                />
                <h1 class="mt-2 text-xl font-semibold">
                  {{ currentUser.first_name }}
                  {{ currentUser.last_name }}
                </h1>
                <div class="tex-sm text-gray-800">
                  {{ currentUser.role | roleDisplay }}
                </div>
              </div>
            </div>
            <div class="flex-1">
              <base-label-text label="Miestas" class="mb-4">
                {{ currentUser.city }}
              </base-label-text>

              <base-label-text label="El. pašto adresas" class="mb-4">
                {{ currentUser.email }}
              </base-label-text>

              <base-label-text label="Telefono numeris" class="mb-4">
                {{ currentUser.phone }}
              </base-label-text>

              <base-label-text
                v-if="currentUser.contract_no"
                label="Sutartis numeris"
                class="mb-4"
              >
                {{ currentUser.contract_no }}
              </base-label-text>

              <base-label-text
                v-if="currentUser.contract_from"
                label="Sutartis nuo - iki"
                class="mb-4"
              >
                {{ currentUser.contract_from }}
                {{ currentUser.contract_to }}
              </base-label-text>
            </div>
          </div>
        </div>

        <form v-if="tab === 'contacts'" @submit.prevent="onSubmitContactForm">
          <base-notification v-if="isSaved" class="mb-4">
            Informacija atnaujinta.
          </base-notification>

          <p v-if="contactErrorMessage" class="text-error mb-4">
            {{ contactErrorMessage }}
          </p>

          <div class="mb-4">
            <base-label-input
              v-model="email"
              type="text"
              required
              :error="failedField === 'email'"
              label="El. pašto adresas"
            />
          </div>

          <div class="mb-4">
            <base-label-input
              v-model="phone"
              type="text"
              required
              :error="failedField === 'phone'"
              label="Tel. numeris"
            />
          </div>

          <div class="mb-4">
            <base-label-input
              type="file"
              :error="failedField === 'file'"
              label="Nuotrauka"
              accept="image/*"
              @change="onFileChange"
            />
          </div>

          <div class="w-full lg:w-48">
            <base-submit :loading="isLoadingContactInfo">
              Atnaujinti
            </base-submit>
          </div>
        </form>

        <form
          v-if="tab === 'password'"
          class="mt-4"
          @submit.prevent="onSubmitPasswordForm"
        >
          <base-notification v-if="isSaved" class="mb-4">
            Informacija atnaujinta.
          </base-notification>

          <p v-if="passwordErrorMessage" class="text-error mb-4">
            {{ passwordErrorMessage }}
          </p>

          <div class="mb-4">
            <base-label-input
              v-model="oldPassword"
              type="password"
              :error="failedField === 'old_password'"
              label="Senas slaptažodis"
            />
          </div>

          <div class="mb-4">
            <base-label-input
              v-model="newPassword"
              type="password"
              :error="failedField === 'new_password'"
              label="Naujas slaptažodis"
            />
          </div>

          <div class="w-full lg:w-48">
            <base-submit :loading="isLoadingPasswordChange">
              Pakeisti slaptažodį
            </base-submit>
          </div>
        </form>

        <template v-if="tab === 'duties'">
          <div class="-mx-4 lg:-mx-8">
            <table class="table-auto w-full">
              <thead>
                <tr class="text-left border-t border-b">
                  <th class="w-40">Data</th>
                  <th class="w-40">Pamaina</th>
                  <th class="w-64">Tipas</th>
                  <th>Vaikas</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="duty in sortedDuties" :key="duty.id">
                  <td>{{ duty.date }}</td>
                  <td>{{ duty.hours }}</td>
                  <td>
                    <template v-if="duty.kid_id"> Budėjimas </template>
                    <template v-else> Galimybė </template>
                  </td>
                  <td>{{ duty.kid_first_name }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-if="tab === 'events'">
          <div class="-mx-4 lg:-mx-8">
            <table class="table-auto w-full">
              <thead>
                <tr class="text-left border-t border-b">
                  <th class="w-48">Data</th>
                  <th class="w-32">Tipas</th>
                  <th class="w-32">Trukmė, val.</th>
                  <th>Pavadinimas</th>
                  <th class="w-48">Būsena</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="registration in events" :key="registration.id">
                  <td>
                    {{ registration.event_date }}
                    <template v-if="registration.event_time">
                      {{ registration.event_time }} val.
                    </template>
                  </td>
                  <td>{{ registration.event_purpose }}</td>
                  <td>{{ registration.event_duration }}</td>
                  <td>{{ registration.event_title }}</td>
                  <td>
                    <template v-if="registration.status === 'registered'">
                      Užsiregistravęs
                    </template>
                    <template v-if="registration.status === 'invited'">
                      Gautas pakvietimas
                    </template>
                    <template v-if="registration.status === 'participated'">
                      Dalyvavo
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-if="tab === 'studies'">
          <div class="-mx-4 lg:-mx-8">
            <table class="table-auto w-full">
              <thead>
                <tr class="text-left border-t border-b">
                  <th class="w-48">Data</th>
                  <th class="w-32">Trukmė, val.</th>
                  <th>Pavadinimas</th>
                  <th class="w-48">Būsena</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="registration in studies" :key="registration.id">
                  <td>
                    {{ registration.event_date }}
                    <template v-if="registration.event_time">
                      {{ registration.event_time }} val.
                    </template>
                  </td>
                  <td>{{ registration.event_duration }}</td>
                  <td>{{ registration.event_title }}</td>
                  <td>
                    <template v-if="registration.status === 'registered'">
                      Užsiregistravęs
                    </template>
                    <template v-if="registration.status === 'invited'">
                      Gautas pakvietimas
                    </template>
                    <template v-if="registration.status === 'participated'">
                      Dalyvavo
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-if="tab === 'hours'">
          <div class="-mx-4 lg:-mx-8">
            <table class="table-auto w-full">
              <thead>
                <tr class="text-left border-t border-b">
                  <th class="w-40">Data</th>
                  <th>Įrašas</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="duty in sortedDuties" :key="duty.id">
                  <td>
                    {{ duty.date }}
                  </td>
                  <td> {{ duty.notes }} ({{ duty.elapsed_hours }} val.) </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-if="tab === 'files'">
          <div class="-mx-4 lg:-mx-8">
            <table class="table-auto w-full">
              <thead>
                <tr class="text-left border-t border-b">
                  <th>Dokumentas</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="file in userFiles" :key="file.id">
                  <td>
                    <base-upload-link :filename="file.file_path">
                      {{ file.file_name }}
                    </base-upload-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </Layout>
</template>
